import type { FC, FocusEvent, MouseEvent, ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import type { MenuProps } from 'antd';
import { Avatar, Button, Col, Dropdown, Row, Space, Typography } from 'antd';
import { TbCopy, TbDotsVertical, TbFilePlus, TbLink, TbNote, TbTopologyStar3, TbUser } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DocumentConceptTag from '@/shared/components/attachments/AttachmentRedactionEditor/DocumentConceptTag';
import type { DocumentConceptReference } from '@/shared/components/attachments/AttachmentRedactionEditor/documentIntelligence';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';

import type { TranscriptOntologyAnnotation, TranscriptSection } from '../../types';

interface TranscriptSectionCardProps {
    activeOntologyReferenceId?: string;
    annotations: readonly TranscriptOntologyAnnotation[];
    executiveTitle?: string;
    memoPanel: ReactElement;
    ontologyEnabled: boolean;
    section: TranscriptSection;
    sourceEnd: number;
    sourceSectionId?: string;
    sourceStart: number;
    onCopy: () => void;
    onCopyLink: () => void;
    onCreateMemo: () => void;
    onOpenOntology: () => void;
    onOpenReferences: () => void;
    onReferenceSelect: (reference: DocumentConceptReference) => void;
    onTextSelection: (event: MouseEvent<HTMLElement>) => void;
}

const renderSourceAwareRange = (
    section: TranscriptSection,
    start: number,
    end: number,
    sourceSectionId: string | undefined,
    sourceStart: number,
    sourceEnd: number,
    sourceBackground: string,
): ReactNode => {
    const text = section.content.slice(start, end);

    if (
        section.id !== sourceSectionId ||
        !Number.isFinite(sourceStart) ||
        !Number.isFinite(sourceEnd) ||
        sourceEnd <= sourceStart
    ) {
        return text;
    }

    const localSourceStart = Math.max(0, sourceStart - section.start);
    const localSourceEnd = Math.min(section.content.length, sourceEnd - section.start);
    const highlightStart = Math.max(start, localSourceStart);
    const highlightEnd = Math.min(end, localSourceEnd);

    if (highlightStart >= highlightEnd) {
        return text;
    }

    return (
        <>
            {section.content.slice(start, highlightStart)}
            <mark
                data-transcript-source-target='true'
                style={{ background: sourceBackground, color: 'inherit', padding: 0 }}
            >
                {section.content.slice(highlightStart, highlightEnd)}
            </mark>
            {section.content.slice(highlightEnd, end)}
        </>
    );
};

const TranscriptSectionText: FC<
    Pick<
        TranscriptSectionCardProps,
        | 'activeOntologyReferenceId'
        | 'annotations'
        | 'ontologyEnabled'
        | 'onReferenceSelect'
        | 'section'
        | 'sourceEnd'
        | 'sourceSectionId'
        | 'sourceStart'
    >
> = ({
    activeOntologyReferenceId,
    annotations,
    ontologyEnabled,
    onReferenceSelect,
    section,
    sourceEnd,
    sourceSectionId,
    sourceStart,
}) => {
    const { token } = useAntDesignTheme();

    if (!ontologyEnabled || !annotations.length) {
        return (
            <>
                {renderSourceAwareRange(
                    section,
                    0,
                    section.content.length,
                    sourceSectionId,
                    sourceStart,
                    sourceEnd,
                    token.colorWarningBg,
                )}
            </>
        );
    }

    const content: ReactNode[] = [];
    let cursor = 0;

    annotations.forEach((annotation) => {
        if (annotation.start > cursor) {
            content.push(
                <span key={`${section.id}:text:${cursor}`}>
                    {renderSourceAwareRange(
                        section,
                        cursor,
                        annotation.start,
                        sourceSectionId,
                        sourceStart,
                        sourceEnd,
                        token.colorWarningBg,
                    )}
                </span>,
            );
        }

        content.push(
            <DocumentConceptTag
                key={annotation.reference.id}
                active={activeOntologyReferenceId === annotation.reference.id}
                concept={annotation.concept}
                domain={annotation.domain}
                reference={annotation.reference}
                text={section.content.slice(annotation.start, annotation.end)}
                onSelect={onReferenceSelect}
            />,
        );
        cursor = annotation.end;
    });

    if (cursor < section.content.length) {
        content.push(
            <span key={`${section.id}:text:${cursor}`}>
                {renderSourceAwareRange(
                    section,
                    cursor,
                    section.content.length,
                    sourceSectionId,
                    sourceStart,
                    sourceEnd,
                    token.colorWarningBg,
                )}
            </span>,
        );
    }

    return <>{content}</>;
};

const TranscriptSectionCard: FC<TranscriptSectionCardProps> = ({
    activeOntologyReferenceId,
    annotations,
    executiveTitle,
    memoPanel,
    onCopy,
    onCopyLink,
    onCreateMemo,
    onOpenOntology,
    onOpenReferences,
    onReferenceSelect,
    onTextSelection,
    ontologyEnabled,
    section,
    sourceEnd,
    sourceSectionId,
    sourceStart,
}) => {
    const { token } = useAntDesignTheme();
    const [actionsFocused, setActionsFocused] = useState(false);
    const [actionsHovered, setActionsHovered] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const actionsVisible = actionsFocused || actionsHovered || dropdownOpen;
    const moreActions: MenuProps['items'] = [
        {
            icon: <TbLink size='1rem' />,
            key: 'copy-link',
            label: 'Copy passage link',
            onClick: onCopyLink,
        },
        {
            icon: <TbFilePlus size='1rem' />,
            key: 'create-memo',
            label: 'Create new Memo',
            onClick: onCreateMemo,
        },
        { type: 'divider' },
        {
            icon: <TbNote size='1rem' />,
            key: 'open-references',
            label: 'Open References',
            onClick: onOpenReferences,
        },
        {
            icon: <TbTopologyStar3 size='1rem' />,
            key: 'open-ontology',
            label: ontologyEnabled ? 'View in Ontology' : 'Enable Ontology Mode',
            onClick: onOpenOntology,
        },
    ];
    const handleBlur = (event: FocusEvent<HTMLElement>) => {
        const nextTarget = event.relatedTarget;

        if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
            setActionsFocused(false);
        }
    };

    return (
        <article
            aria-label={`${section.speaker} transcript passage`}
            data-end={section.end}
            data-start={section.start}
            id={section.id}
            tabIndex={0}
            onBlurCapture={handleBlur}
            onFocusCapture={() => setActionsFocused(true)}
            onMouseEnter={() => setActionsHovered(true)}
            onMouseLeave={() => setActionsHovered(false)}
            style={{
                background: token.colorBgContainer,
                border: `1px solid ${actionsVisible ? token.colorBorder : token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                marginBottom: '1rem',
                padding: '1rem',
                transition: `border-color ${token.motionDurationFast}`,
            }}
        >
            <Row align='top' gutter={[12, 0]} wrap={false}>
                <Col flex='none'>
                    <Avatar
                        icon={<TbUser style={{ color: token.colorTextTertiary }} />}
                        size='large'
                        style={{
                            background: token.colorBgContainer,
                            border: `1px dashed ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Row align='middle' gutter={8} wrap={false}>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <Typography.Text strong>{section.speaker}</Typography.Text>
                            {executiveTitle ? (
                                <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                    {executiveTitle}
                                </small>
                            ) : null}
                        </Col>
                        <Col flex='none'>
                            <Space.Compact
                                aria-label={`Actions for ${section.speaker}`}
                                role='group'
                                size='small'
                                style={{
                                    opacity: actionsVisible ? 1 : 0,
                                    pointerEvents: actionsVisible ? 'auto' : 'none',
                                    transition: `opacity ${token.motionDurationFast}`,
                                    visibility: actionsVisible ? 'visible' : 'hidden',
                                }}
                            >
                                <Button icon={<TbCopy style={{ color: token.colorTextTertiary }} />} onClick={onCopy}>
                                    Copy
                                </Button>
                                <Dropdown
                                    destroyOnHidden
                                    popupRender={() => memoPanel}
                                    placement='bottomRight'
                                    trigger={['click']}
                                    onOpenChange={setDropdownOpen}
                                >
                                    <Button icon={<TbNote style={{ color: token.colorTextTertiary }} />}>
                                        Add to Memo
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    destroyOnHidden
                                    popupRender={() => <DropdownMenuPanel items={moreActions} />}
                                    placement='bottomRight'
                                    trigger={['click']}
                                    onOpenChange={setDropdownOpen}
                                >
                                    <Button
                                        aria-label={`More actions for ${section.speaker}`}
                                        icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                                    />
                                </Dropdown>
                            </Space.Compact>
                        </Col>
                    </Row>
                    <Typography.Paragraph
                        data-transcript-section-content='true'
                        onMouseUp={onTextSelection}
                        style={{
                            color: token.colorTextSecondary,
                            margin: '0.625rem 0 0',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        <TranscriptSectionText
                            activeOntologyReferenceId={activeOntologyReferenceId}
                            annotations={annotations}
                            ontologyEnabled={ontologyEnabled}
                            section={section}
                            sourceEnd={sourceEnd}
                            sourceSectionId={sourceSectionId}
                            sourceStart={sourceStart}
                            onReferenceSelect={onReferenceSelect}
                        />
                    </Typography.Paragraph>
                </Col>
            </Row>
        </article>
    );
};

export default TranscriptSectionCard;
